import { z } from "zod";
import { AGENT_IDS, AGENTS, type AgentId } from "./nav.js";
import type { ChatMessage } from "./messages.js";

/**
 * Shapes match a future `/api/approvals?status=pending` and recents feed.
 * Matrix uses stubs until Approval DO / audit are bound.
 */
export const PendingApprovalSchema = z.object({
	id: z.string().min(1),
	agentId: z.enum(AGENT_IDS),
	summary: z.string().min(1),
	tier: z.enum(["tier-1", "tier-2", "tier-3"]),
	estimatedCostUsd: z.number().nonnegative().optional(),
	createdAt: z.string().datetime(),
});

export type PendingApproval = z.infer<typeof PendingApprovalSchema>;

export const RecentChatSchema = z.object({
	agentId: z.enum(AGENT_IDS),
	preview: z.string().min(1),
	at: z.string().datetime(),
});

export type RecentChat = z.infer<typeof RecentChatSchema>;

/** Demo pending gates — replace with Approval DO list. */
export function stubPendingApprovals(setupDone: boolean): PendingApproval[] {
	if (!setupDone) return [];

	const now = Date.now();
	const raw: PendingApproval[] = [
		{
			id: "stub-appr-budget",
			agentId: "paid_media",
			summary: "Raise Search daily budget by $25 (PAUSED campaign)",
			tier: "tier-2",
			estimatedCostUsd: 25,
			createdAt: new Date(now - 12 * 60_000).toISOString(),
		},
		{
			id: "stub-appr-rsa",
			agentId: "paid_media",
			summary: "Create responsive search ad draft for brand terms",
			tier: "tier-1",
			createdAt: new Date(now - 48 * 60_000).toISOString(),
		},
	];

	return raw.flatMap((row) => {
		const parsed = PendingApprovalSchema.safeParse(row);
		return parsed.success ? [parsed.data] : [];
	});
}

function previewFromMessages(messages: ChatMessage[] | undefined): {
	preview: string;
	at: string;
} | null {
	if (!messages || messages.length === 0) return null;
	const last = messages[messages.length - 1];
	if (!last) return null;
	const text =
		last.body.trim() ||
		last.card?.title ||
		last.card?.body ||
		"Open conversation";
	const preview =
		text.length > 72 ? `${text.slice(0, 69).trimEnd()}…` : text;
	return { preview, at: last.at };
}

/**
 * Prefer real local chat timelines; fill with live agents that have seeds.
 * Caps at `limit` (newest first).
 */
export function recentChatsFromMessages(
	messagesByAgent: Record<string, ChatMessage[]>,
	limit = 5,
): RecentChat[] {
	const liveIds = AGENTS.filter((a) => a.availability === "live").map(
		(a) => a.id,
	);

	const rows: RecentChat[] = [];
	for (const agentId of liveIds) {
		const fromMsgs = previewFromMessages(messagesByAgent[agentId]);
		if (!fromMsgs) continue;
		const row = RecentChatSchema.safeParse({
			agentId,
			preview: fromMsgs.preview,
			at: fromMsgs.at,
		});
		if (row.success) rows.push(row.data);
	}

	rows.sort(
		(a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
	);
	return rows.slice(0, limit);
}

export function tierShort(tier: PendingApproval["tier"]): string {
	switch (tier) {
		case "tier-1":
			return "Review";
		case "tier-2":
			return "Approve";
		case "tier-3":
			return "Confirm";
		default: {
			const _exhaustive: never = tier;
			return _exhaustive;
		}
	}
}

export function relativeTime(iso: string, now = Date.now()): string {
	const ms = now - new Date(iso).getTime();
	if (!Number.isFinite(ms) || ms < 0) return "just now";
	const mins = Math.floor(ms / 60_000);
	if (mins < 1) return "just now";
	if (mins < 60) return `${mins}m`;
	const hours = Math.floor(mins / 60);
	if (hours < 24) return `${hours}h`;
	const days = Math.floor(hours / 24);
	return `${days}d`;
}

export function isLiveAgentId(id: AgentId): boolean {
	return AGENTS.some((a) => a.id === id && a.availability === "live");
}
